import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Triangle, Square, Circle as CircleIcon, Minus, Type, Trash2, RotateCcw, Spline, Save, X, Pencil, Sigma } from "lucide-react";

// Fixed drawing canvas — matches the AI figure viewBox so both look consistent.
const VBW = 300;
const VBH = 220;
let _seq = 0;
const uid = () => `s${Date.now()}${_seq++}`;
const centroid = (pts) => [
  pts.reduce((a, p) => a + p[0], 0) / pts.length,
  pts.reduce((a, p) => a + p[1], 0) / pts.length,
];
const round = (n) => Math.round(n * 10) / 10;

// Snap a straightened stroke's endpoint to the nearest 45° (locks exact
// horizontal 0°/180° and vertical 90°/270°) when it's within ~9° of one.
function snapLine(start, end) {
  const dx = end[0] - start[0], dy = end[1] - start[1];
  const len = Math.hypot(dx, dy);
  if (len < 2) return end;
  const deg = Math.atan2(dy, dx) * 180 / Math.PI;
  const nearest = Math.round(deg / 45) * 45;
  if (Math.abs(deg - nearest) <= 9) {
    const r = nearest * Math.PI / 180;
    return [start[0] + len * Math.cos(r), start[1] + len * Math.sin(r)];
  }
  return end;
}

// Signed angle helpers for the "angle" shape (vertex V + two rays to P1/P2).
function angleDeg(v, p1, p2) {
  const a1 = Math.atan2(p1[1] - v[1], p1[0] - v[0]);
  const a2 = Math.atan2(p2[1] - v[1], p2[0] - v[0]);
  let d = (a2 - a1) * 180 / Math.PI;
  d = Math.abs(((d + 180) % 360) - 180);
  return Math.round(Math.abs(d) > 180 ? 360 - Math.abs(d) : Math.abs(d));
}
function arcData(v, p1, p2, r) {
  const a1 = Math.atan2(p1[1] - v[1], p1[0] - v[0]);
  const a2 = Math.atan2(p2[1] - v[1], p2[0] - v[0]);
  let diff = a2 - a1;
  while (diff <= -Math.PI) diff += 2 * Math.PI;
  while (diff > Math.PI) diff -= 2 * Math.PI;
  const sweep = diff > 0 ? 1 : 0;
  const s = [v[0] + r * Math.cos(a1), v[1] + r * Math.sin(a1)];
  const e = [v[0] + r * Math.cos(a2), v[1] + r * Math.sin(a2)];
  const mid = a1 + diff / 2;
  const label = [v[0] + (r + 16) * Math.cos(mid), v[1] + (r + 16) * Math.sin(mid) + 4];
  return { d: `M ${round(s[0])} ${round(s[1])} A ${r} ${r} 0 0 ${sweep} ${round(e[0])} ${round(e[1])}`, label };
}

function defaultShape(type) {
  switch (type) {
    case "triangle": return { id: uid(), type, pts: [[60, 175], [240, 175], [150, 55]] };
    case "rect": return { id: uid(), type, x: 90, y: 70, w: 120, h: 80 };
    case "circle": return { id: uid(), type, cx: 150, cy: 110, r: 60 };
    case "line": return { id: uid(), type, x1: 60, y1: 110, x2: 240, y2: 110 };
    case "label": return { id: uid(), type, x: 150, y: 110, text: "label" };
    case "angle": {
      const v = [80, 175], p1 = [250, 175], p2 = [160, 55];
      return { id: uid(), type, v, p1, p2, deg: `${angleDeg(v, p1, p2)}°` };
    }
    default: return null;
  }
}

// vertex labels A,B,C… placed just outside a polygon
function polyLabels(pts) {
  const c = centroid(pts);
  return pts.map((p, i) => {
    const dx = p[0] - c[0], dy = p[1] - c[1];
    const L = Math.hypot(dx, dy) || 1;
    return { x: p[0] + (dx / L) * 15, y: p[1] + (dy / L) * 15 + 4, ch: String.fromCharCode(65 + i) };
  });
}

function shapeToSvg(s) {
  const st = 'fill="none" stroke="#111" stroke-width="2"';
  if (s.type === "triangle") {
    const poly = `<polygon points="${s.pts.map((p) => `${round(p[0])},${round(p[1])}`).join(" ")}" ${st}/>`;
    const labels = polyLabels(s.pts)
      .map((l) => `<text x="${round(l.x)}" y="${round(l.y)}" font-size="14" fill="#111" text-anchor="middle">${l.ch}</text>`)
      .join("");
    return poly + labels;
  }
  if (s.type === "rect") return `<rect x="${round(s.x)}" y="${round(s.y)}" width="${round(s.w)}" height="${round(s.h)}" ${st}/>`;
  if (s.type === "circle") return `<circle cx="${round(s.cx)}" cy="${round(s.cy)}" r="${round(s.r)}" ${st}/>`;
  if (s.type === "line") return `<line x1="${round(s.x1)}" y1="${round(s.y1)}" x2="${round(s.x2)}" y2="${round(s.y2)}" stroke="#111" stroke-width="2"/>`;
  if (s.type === "path") return `<polyline points="${s.pts.map((p) => `${round(p[0])},${round(p[1])}`).join(" ")}" fill="none" stroke="#111" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`;
  if (s.type === "label") return `<text x="${round(s.x)}" y="${round(s.y)}" font-size="14" fill="#111" text-anchor="middle">${(s.text || "").replace(/[<>&]/g, "")}</text>`;
  if (s.type === "angle") {
    const { d, label } = arcData(s.v, s.p1, s.p2, 30);
    const line = (a, b) => `<line x1="${round(a[0])}" y1="${round(a[1])}" x2="${round(b[0])}" y2="${round(b[1])}" stroke="#111" stroke-width="2"/>`;
    const arc = `<path d="${d}" fill="none" stroke="#111" stroke-width="1.5"/>`;
    const txt = `<text x="${round(label[0])}" y="${round(label[1])}" font-size="13" fill="#111" text-anchor="middle">${(s.deg || "").replace(/[<>&]/g, "")}</text>`;
    return line(s.v, s.p1) + line(s.v, s.p2) + arc + txt;
  }
  return "";
}

export function shapesToSvg(shapes) {
  if (!shapes?.length) return "";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VBW} ${VBH}">${shapes.map(shapeToSvg).join("")}</svg>`;
}

const TOOLS = [
  { type: "triangle", icon: Triangle, label: "Triangle" },
  { type: "rect", icon: Square, label: "Rectangle" },
  { type: "circle", icon: CircleIcon, label: "Circle" },
  { type: "angle", icon: Spline, label: "Angle" },
  { type: "line", icon: Minus, label: "Line" },
  { type: "label", icon: Type, label: "Text" },
];

export default function FigureEditor({ open, initialShapes, onClose, onSave }) {
  const [shapes, setShapes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [library, setLibrary] = useState([]);
  const [libName, setLibName] = useState("");
  const [savingLib, setSavingLib] = useState(false);
  const [mode, setMode] = useState("select"); // "select" | "draw" (freehand)
  const [showSymbols, setShowSymbols] = useState(false);
  const svgRef = useRef(null);
  const dragRef = useRef(null);

  const loadLibrary = async () => {
    try { const { data } = await api.get("/teacher/figures"); setLibrary(data.figures || []); } catch { /* noop */ }
  };

  useEffect(() => {
    if (open) {
      setShapes(Array.isArray(initialShapes) ? JSON.parse(JSON.stringify(initialShapes)) : []);
      setSelected(null);
      setLibName("");
      setMode("select");
      setShowSymbols(false);
      loadLibrary();
    }
  }, [open, initialShapes]);

  const saveToLibrary = async () => {
    if (!shapes.length) { toast.error("Draw something first."); return; }
    if (!libName.trim()) { toast.error("Give the figure a name."); return; }
    setSavingLib(true);
    try {
      await api.post("/teacher/figures", { name: libName.trim(), svg: shapesToSvg(shapes), shapes });
      setLibName("");
      await loadLibrary();
      toast.success("Saved to your figure library");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not save figure"); }
    finally { setSavingLib(false); }
  };
  const loadFromLibrary = (f) => {
    if (!f.shapes?.length) { toast.error("This saved figure can't be edited."); return; }
    setShapes(JSON.parse(JSON.stringify(f.shapes)));
    setSelected(null);
    toast.success(`Loaded "${f.name}"`);
  };
  const deleteFromLibrary = async (id) => {
    try { await api.delete(`/teacher/figures/${id}`); setLibrary((p) => p.filter((f) => f.id !== id)); } catch { /* noop */ }
  };

  const toXY = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * VBW, ((e.clientY - r.top) / r.height) * VBH];
  };

  const add = (type) => {
    setMode("select");
    const s = defaultShape(type);
    if (!s) return;
    setShapes((p) => [...p, s]);
    setSelected(s.id);
  };

  const MATH_SYMBOLS = ["√", "π", "∑", "∫", "∞", "±", "×", "÷", "≈", "≠", "≤", "≥", "°", "∠", "△", "θ", "α", "β", "Δ", "→", "²", "³", "½", "¼", "∴", "⊥", "∥", "≅", "∈", "·"];
  const insertSymbol = (sym) => {
    const sel = shapes.find((s) => s.id === selected);
    if (sel && sel.type === "label") { patch(sel.id, (s) => ({ ...s, text: (s.text || "") + sym })); return; }
    if (sel && sel.type === "angle") { patch(sel.id, (s) => ({ ...s, deg: (s.deg || "") + sym })); return; }
    const s = { id: uid(), type: "label", x: 150, y: 110, text: sym };
    setShapes((p) => [...p, s]);
    setSelected(s.id);
  };

  const patch = (id, fn) => setShapes((p) => p.map((s) => (s.id === id ? fn(s) : s)));
  const remove = (id) => { setShapes((p) => p.filter((s) => s.id !== id)); setSelected(null); };

  // Freehand drawing: start a stroke on canvas pointer-down while in draw mode.
  const startDrawStroke = (e) => {
    if (mode !== "draw") { setSelected(null); return; }
    e.stopPropagation();
    const [x, y] = toXY(e);
    const s = { id: uid(), type: "path", pts: [[x, y]] };
    setShapes((p) => [...p, s]);
    dragRef.current = { id: s.id, kind: "draw", start: [x, y], straight: false, lastXY: [x, y], holdTimer: null };
  };

  const startMove = (e, s) => {
    e.stopPropagation();
    setSelected(s.id);
    dragRef.current = { id: s.id, kind: "move", start: toXY(e), orig: JSON.parse(JSON.stringify(s)) };
  };
  const startHandle = (e, s, handle) => {
    e.stopPropagation();
    setSelected(s.id);
    dragRef.current = { id: s.id, kind: "handle", handle, orig: JSON.parse(JSON.stringify(s)) };
  };

  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const [x, y] = toXY(e);
    if (d.kind === "draw") {
      d.lastXY = [x, y];
      if (d.straight) {
        patch(d.id, (s) => ({ ...s, pts: [d.start, snapLine(d.start, [x, y])] }));
      } else {
        patch(d.id, (s) => ({ ...s, pts: [...s.pts, [x, y]] }));
      }
      // Hold still ~350ms to snap the stroke into a straight line.
      if (d.holdTimer) clearTimeout(d.holdTimer);
      d.holdTimer = setTimeout(() => {
        d.straight = true;
        patch(d.id, (s) => ({ ...s, pts: [d.start, snapLine(d.start, d.lastXY)] }));
      }, 350);
      return;
    }
    if (d.kind === "handle") {
      const o = d.orig;
      patch(d.id, (s) => {
        const n = { ...s };
        if (o.type === "triangle") { const pts = o.pts.map((p) => [...p]); pts[d.handle] = [x, y]; n.pts = pts; }
        else if (o.type === "line") { if (d.handle === 0) { n.x1 = x; n.y1 = y; } else { n.x2 = x; n.y2 = y; } }
        else if (o.type === "rect") {
          if (d.handle === "br") { n.w = Math.max(10, x - o.x); n.h = Math.max(10, y - o.y); }
          else { const nx = Math.min(x, o.x + o.w - 10), ny = Math.min(y, o.y + o.h - 10); n.x = nx; n.y = ny; n.w = o.x + o.w - nx; n.h = o.y + o.h - ny; }
        }
        else if (o.type === "circle") { n.r = Math.max(6, Math.hypot(x - o.cx, y - o.cy)); }
        else if (o.type === "angle") {
          if (d.handle === "v") n.v = [x, y];
          else if (d.handle === 0) n.p1 = [x, y];
          else n.p2 = [x, y];
          n.deg = `${angleDeg(n.v, n.p1, n.p2)}°`;
        }
        return n;
      });
    } else if (d.kind === "move") {
      const dx = x - d.start[0], dy = y - d.start[1];
      const o = d.orig;
      patch(d.id, (s) => {
        const n = { ...s };
        if (o.type === "triangle") n.pts = o.pts.map((p) => [p[0] + dx, p[1] + dy]);
        else if (o.type === "line") { n.x1 = o.x1 + dx; n.y1 = o.y1 + dy; n.x2 = o.x2 + dx; n.y2 = o.y2 + dy; }
        else if (o.type === "rect") { n.x = o.x + dx; n.y = o.y + dy; }
        else if (o.type === "circle") { n.cx = o.cx + dx; n.cy = o.cy + dy; }
        else if (o.type === "label") { n.x = o.x + dx; n.y = o.y + dy; }
        else if (o.type === "path") { n.pts = o.pts.map((p) => [p[0] + dx, p[1] + dy]); }
        else if (o.type === "angle") { n.v = [o.v[0] + dx, o.v[1] + dy]; n.p1 = [o.p1[0] + dx, o.p1[1] + dy]; n.p2 = [o.p2[0] + dx, o.p2[1] + dy]; }
        return n;
      });
    }
  };
  const endDrag = () => {
    const d = dragRef.current;
    if (d && d.kind === "draw") {
      if (d.holdTimer) clearTimeout(d.holdTimer);
      // Discard strokes that are just a dot.
      setShapes((p) => p.filter((s) => !(s.id === d.id && s.type === "path" && s.pts.length < 2)));
    }
    dragRef.current = null;
  };

  const Handle = ({ x, y, on }) => (
    <circle cx={x} cy={y} r="6" fill="#2563eb" stroke="#fff" strokeWidth="1.5" className="cursor-pointer"
      onPointerDown={on} style={{ touchAction: "none" }} />
  );

  const renderShape = (s) => {
    const isSel = selected === s.id;
    const stroke = isSel ? "#2563eb" : "#111";
    const common = { stroke, strokeWidth: 2, fill: "none", className: "cursor-move", style: { touchAction: "none" } };
    const parts = [];
    if (s.type === "triangle") {
      parts.push(<polygon key="b" points={s.pts.map((p) => p.join(",")).join(" ")} {...common} onPointerDown={(e) => startMove(e, s)} />);
      polyLabels(s.pts).forEach((l, i) => parts.push(<text key={`l${i}`} x={l.x} y={l.y} fontSize="14" fill="#111" textAnchor="middle" style={{ pointerEvents: "none" }}>{l.ch}</text>));
      if (isSel) s.pts.forEach((p, i) => parts.push(<Handle key={`h${i}`} x={p[0]} y={p[1]} on={(e) => startHandle(e, s, i)} />));
    } else if (s.type === "rect") {
      parts.push(<rect key="b" x={s.x} y={s.y} width={s.w} height={s.h} {...common} onPointerDown={(e) => startMove(e, s)} />);
      if (isSel) { parts.push(<Handle key="tl" x={s.x} y={s.y} on={(e) => startHandle(e, s, "tl")} />); parts.push(<Handle key="br" x={s.x + s.w} y={s.y + s.h} on={(e) => startHandle(e, s, "br")} />); }
    } else if (s.type === "circle") {
      parts.push(<circle key="b" cx={s.cx} cy={s.cy} r={s.r} {...common} onPointerDown={(e) => startMove(e, s)} />);
      if (isSel) parts.push(<Handle key="r" x={s.cx + s.r} y={s.cy} on={(e) => startHandle(e, s, "r")} />);
    } else if (s.type === "line") {
      parts.push(<line key="b" x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={stroke} strokeWidth={2} className="cursor-move" style={{ touchAction: "none" }} onPointerDown={(e) => startMove(e, s)} />);
      if (isSel) { parts.push(<Handle key="0" x={s.x1} y={s.y1} on={(e) => startHandle(e, s, 0)} />); parts.push(<Handle key="1" x={s.x2} y={s.y2} on={(e) => startHandle(e, s, 1)} />); }
    } else if (s.type === "label") {
      parts.push(<text key="b" x={s.x} y={s.y} fontSize="14" fill={isSel ? "#2563eb" : "#111"} textAnchor="middle" className="cursor-move" style={{ touchAction: "none" }} onPointerDown={(e) => startMove(e, s)}>{s.text || "label"}</text>);
    } else if (s.type === "path") {
      parts.push(<polyline key="b" points={s.pts.map((p) => p.join(",")).join(" ")} fill="none" stroke={stroke} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" className="cursor-move" style={{ touchAction: "none" }} onPointerDown={(e) => startMove(e, s)} />);
    } else if (s.type === "angle") {
      const arc = arcData(s.v, s.p1, s.p2, 30);
      const ln = (a, b, k) => <line key={k} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={stroke} strokeWidth={2} className="cursor-move" style={{ touchAction: "none" }} onPointerDown={(e) => startMove(e, s)} />;
      parts.push(ln(s.v, s.p1, "r1"), ln(s.v, s.p2, "r2"));
      parts.push(<path key="arc" d={arc.d} fill="none" stroke={stroke} strokeWidth={1.5} style={{ pointerEvents: "none" }} />);
      parts.push(<text key="deg" x={arc.label[0]} y={arc.label[1]} fontSize="13" fill="#111" textAnchor="middle" style={{ pointerEvents: "none" }}>{s.deg}</text>);
      if (isSel) {
        parts.push(<Handle key="v" x={s.v[0]} y={s.v[1]} on={(e) => startHandle(e, s, "v")} />);
        parts.push(<Handle key="p1" x={s.p1[0]} y={s.p1[1]} on={(e) => startHandle(e, s, 0)} />);
        parts.push(<Handle key="p2" x={s.p2[0]} y={s.p2[1]} on={(e) => startHandle(e, s, 1)} />);
      }
    }
    return <g key={s.id}>{parts}</g>;
  };

  const selShape = shapes.find((s) => s.id === selected);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="figure-editor">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Draw / edit figure</DialogTitle>
          <DialogDescription>Add shapes, drag to position, drag the blue dots to resize. Add text for labels &amp; measurements.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2" data-testid="figure-tools">
          {TOOLS.map((t) => (
            <Button key={t.type} size="sm" variant="outline" className="rounded-full h-8" onClick={() => add(t.type)} data-testid={`fig-add-${t.type}`}>
              <t.icon className="h-3.5 w-3.5 mr-1.5" /> {t.label}
            </Button>
          ))}
          <Button size="sm" variant={mode === "draw" ? "default" : "outline"} className={`rounded-full h-8 ${mode === "draw" ? "bg-indigo-500 hover:bg-indigo-600 text-white" : ""}`} onClick={() => { setMode((m) => (m === "draw" ? "select" : "draw")); setSelected(null); }} data-testid="fig-add-freehand">
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Freehand
          </Button>
          <Button size="sm" variant={showSymbols ? "default" : "outline"} className={`rounded-full h-8 ${showSymbols ? "bg-slate-800 text-white" : ""}`} onClick={() => setShowSymbols((v) => !v)} data-testid="fig-symbols-toggle">
            <Sigma className="h-3.5 w-3.5 mr-1.5" /> Symbols
          </Button>
          <Button size="sm" variant="ghost" className="rounded-full h-8 text-slate-500 ml-auto" onClick={() => { setShapes([]); setSelected(null); setMode("select"); }} data-testid="fig-clear">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Clear
          </Button>
        </div>

        {showSymbols && (
          <div className="flex flex-wrap gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 p-2 bg-slate-50 dark:bg-slate-900/40" data-testid="fig-symbol-palette">
            {MATH_SYMBOLS.map((sym) => (
              <button key={sym} onClick={() => insertSymbol(sym)} className="h-8 w-8 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-base hover:border-indigo-400 hover:text-indigo-500 transition-colors" data-testid={`fig-symbol-${sym}`}>{sym}</button>
            ))}
            <span className="self-center text-[11px] text-slate-400 ml-1">Click to add — or select a text/angle first to append</span>
          </div>
        )}

        {mode === "draw" && (
          <div className="text-[11px] text-indigo-500 -mb-1" data-testid="fig-draw-hint">Freehand mode on — draw on the canvas. Hold still for a moment to snap into a straight line. Tap "Freehand" again to move/select.</div>
        )}

        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white overflow-hidden mt-1">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VBW} ${VBH}`}
            className={`w-full h-auto touch-none select-none ${mode === "draw" ? "cursor-crosshair" : ""}`}
            onPointerMove={onMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            onPointerDown={startDrawStroke}
            data-testid="figure-canvas"
          >
            <rect x="0" y="0" width={VBW} height={VBH} fill="#fff" />
            {shapes.map(renderShape)}
          </svg>
        </div>

        {selShape && (
          <div className="flex items-center gap-2 mt-1" data-testid="figure-selected-tools">
            {selShape.type === "label" && (
              <Input value={selShape.text} onChange={(e) => patch(selShape.id, (s) => ({ ...s, text: e.target.value }))} placeholder="Label text (e.g. 5 cm, 60°)" className="h-9 rounded-lg" data-testid="fig-label-text" />
            )}
            {selShape.type === "angle" && (
              <Input value={selShape.deg} onChange={(e) => patch(selShape.id, (s) => ({ ...s, deg: e.target.value }))} placeholder="Angle label (e.g. 60°)" className="h-9 rounded-lg w-40" data-testid="fig-angle-deg" />
            )}
            <Button size="sm" variant="outline" className="rounded-full h-9 text-rose-500 border-rose-200 hover:bg-rose-50" onClick={() => remove(selShape.id)} data-testid="fig-delete">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete shape
            </Button>
          </div>
        )}

        <div className="border-t border-slate-200 dark:border-slate-700 pt-3 mt-1 space-y-2" data-testid="figure-library">
          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400">My figure library</div>
          {library.length > 0 ? (
            <div className="flex gap-2 flex-wrap">
              {library.map((f) => (
                <span key={f.id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 pl-1 pr-1 py-0.5" data-testid={`fig-lib-item-${f.id}`}>
                  <span className="h-7 w-9 rounded bg-white border border-slate-100 overflow-hidden [&_svg]:h-full [&_svg]:w-full" dangerouslySetInnerHTML={{ __html: f.svg }} />
                  <button onClick={() => loadFromLibrary(f)} className="text-xs font-medium text-slate-600 dark:text-slate-200 hover:text-indigo-500 px-1 max-w-[110px] truncate" title={`Load "${f.name}"`} data-testid={`fig-lib-load-${f.id}`}>{f.name}</button>
                  <button onClick={() => deleteFromLibrary(f.id)} className="text-slate-300 hover:text-rose-500" title="Delete" data-testid={`fig-lib-del-${f.id}`}><X className="h-3.5 w-3.5" /></button>
                </span>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-400">No saved figures yet. Draw one and save it to reuse across papers.</div>
          )}
          <div className="flex items-center gap-2">
            <Input value={libName} onChange={(e) => setLibName(e.target.value)} placeholder="Name to save current figure…" className="h-9 rounded-lg" data-testid="fig-lib-name" />
            <Button size="sm" variant="outline" className="rounded-full h-9 shrink-0" onClick={saveToLibrary} disabled={savingLib || !shapes.length} data-testid="fig-lib-save">
              <Save className="h-3.5 w-3.5 mr-1.5" /> Save to library
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={() => onSave(shapesToSvg(shapes), shapes)} className="rounded-full bg-indigo-500 hover:bg-indigo-600" data-testid="fig-save">
            Use this figure
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
